/**
 * Base Maps List Component
 * 
 * Displays a table of all base maps with filtering, sorting, and actions.
 * Requirements: 28.1, 28.2, 28.3, 28.4, 28.5, 28.6
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Box,
  SpaceBetween,
  Button,
  Header,
  Pagination,
  TextFilter,
  Select,
  StatusIndicator,
  Link
} from '@cloudscape-design/components';
import { baseMapsApi } from '../../services/baseMapsApi';
import type { BaseMap } from '../../types/baseMaps';

const BaseMapsList: React.FC = () => {
  const navigate = useNavigate();
  const [baseMaps, setBaseMaps] = useState<BaseMap[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filteringText, setFilteringText] = useState('');
  const [typeFilter, setTypeFilter] = useState<{ label: string; value: string } | null>(null);
  const [sortingColumn, setSortingColumn] = useState<string>('name');
  const [sortingDescending, setSortingDescending] = useState(false);

  useEffect(() => {
    loadBaseMaps();
  }, []);

  const loadBaseMaps = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await baseMapsApi.listBaseMaps();
      
      if (response.error) {
        setError(response.error);
      } else if (response.data) {
        setBaseMaps(response.data.baseMaps || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load base maps');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (baseMap: BaseMap) => {
    if (!confirm(`Are you sure you want to delete "${baseMap.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await baseMapsApi.deleteBaseMap(baseMap.id);
      
      if (response.error) {
        alert(`Failed to delete base map: ${response.error}`);
      } else {
        await loadBaseMaps();
      }
    } catch (err) {
      alert(`Failed to delete base map: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  // Filter and sort base maps
  const filteredAndSortedBaseMaps = React.useMemo(() => {
    let filtered = baseMaps;

    // Apply text filter
    if (filteringText) {
      const lowerFilter = filteringText.toLowerCase();
      filtered = filtered.filter(
        (bm) =>
          bm.name.toLowerCase().includes(lowerFilter) ||
          bm.id.toLowerCase().includes(lowerFilter) ||
          bm.description?.toLowerCase().includes(lowerFilter)
      );
    }

    // Apply type filter
    if (typeFilter && typeFilter.value !== 'all') {
      filtered = filtered.filter((bm) => bm.type === typeFilter.value);
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortingColumn) {
        case 'name':
          aValue = a.name;
          bValue = b.name;
          break;
        case 'type':
          aValue = a.type;
          bValue = b.type;
          break;
        case 'updatedAt':
          aValue = new Date(a.updatedAt).getTime();
          bValue = new Date(b.updatedAt).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortingDescending ? 1 : -1;
      if (aValue > bValue) return sortingDescending ? -1 : 1;
      return 0;
    });

    return sorted;
  }, [baseMaps, filteringText, typeFilter, sortingColumn, sortingDescending]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getCoverageText = (baseMap: BaseMap) => {
    const { bounds } = baseMap;
    if (!bounds) {
      return 'Multi-tile (varies by tile)';
    }
    return `${bounds.north.toFixed(2)}°N to ${bounds.south.toFixed(2)}°N, ${Math.abs(bounds.west).toFixed(2)}°W to ${Math.abs(bounds.east).toFixed(2)}°W`;
  };

  const getProjectionText = (baseMap: BaseMap) => {
    const typeMap: Record<string, string> = {
      lcc: 'Lambert Conformal Conic',
      mercator: 'Mercator',
      albers: 'Albers Equal Area',
      utm: 'UTM'
    };
    return typeMap[baseMap.projection.type] || baseMap.projection.type;
  };

  return (
    <Table
      loading={loading}
      loadingText="Loading base maps..."
      columnDefinitions={[
        {
          id: 'thumbnail',
          header: 'Thumbnail',
          cell: (item) => (
            <img
              src={item.thumbnailUrl || item.thumbnailPath}
              alt={item.name}
              style={{ width: '80px', height: '50px', objectFit: 'cover' }}
            />
          ),
          width: 100,
          minWidth: 100
        },
        {
          id: 'name',
          header: 'Name',
          cell: (item) => (
            <Link onFollow={() => navigate(`/admin/base-maps/${item.id}`)}>
              {item.name}
            </Link>
          ),
          sortingField: 'name'
        },
        {
          id: 'type',
          header: 'Type',
          cell: (item) => (
            <Box>
              {item.type === 'single-tile' ? (
                <StatusIndicator type="success">Single Tile</StatusIndicator>
              ) : (
                <StatusIndicator type="info">Multi-Tile</StatusIndicator>
              )}
            </Box>
          ),
          sortingField: 'type'
        },
        {
          id: 'coverage',
          header: 'Coverage',
          cell: (item) => getCoverageText(item),
          width: 250
        },
        {
          id: 'projection',
          header: 'Projection',
          cell: (item) => getProjectionText(item)
        },
        {
          id: 'updatedAt',
          header: 'Last Updated',
          cell: (item) => formatDate(item.updatedAt),
          sortingField: 'updatedAt'
        },
        {
          id: 'actions',
          header: 'Actions',
          cell: (item) => (
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="inline-link"
                onClick={() => navigate(`/admin/base-maps/${item.id}`)}
              >
                View Details
              </Button>
              <Button
                variant="inline-link"
                onClick={() => navigate(`/admin/base-maps/${item.id}/edit`)}
              >
                Edit
              </Button>
              <Button
                variant="inline-link"
                onClick={() => handleDelete(item)}
              >
                Delete
              </Button>
            </SpaceBetween>
          ),
          width: 200
        }
      ]}
      items={filteredAndSortedBaseMaps}
      sortingColumn={{ sortingField: sortingColumn }}
      sortingDescending={sortingDescending}
      onSortingChange={({ detail }) => {
        setSortingColumn(detail.sortingColumn.sortingField || 'name');
        setSortingDescending(detail.isDescending || false);
      }}
      header={
        <Header
          variant="h2"
          counter={`(${filteredAndSortedBaseMaps.length})`}
          actions={
            <Button
              variant="primary"
              onClick={() => navigate('/admin/base-maps/create')}
            >
              Create Base Map
            </Button>
          }
        >
          Base Maps
        </Header>
      }
      filter={
        <SpaceBetween direction="horizontal" size="m">
          <TextFilter
            filteringText={filteringText}
            filteringPlaceholder="Search base maps"
            onChange={({ detail }) => setFilteringText(detail.filteringText)}
          />
          <Select
            selectedOption={typeFilter}
            onChange={({ detail }) => setTypeFilter(detail.selectedOption as { label: string; value: string; } | null)}
            options={[
              { label: 'All Types', value: 'all' },
              { label: 'Single Tile', value: 'single-tile' },
              { label: 'Multi-Tile', value: 'multi-tile' }
            ]}
            placeholder="Filter by type"
          />
        </SpaceBetween>
      }
      empty={
        <Box textAlign="center" color="inherit">
          <Box padding={{ bottom: 's' }} variant="p" color="inherit">
            {error ? (
              <StatusIndicator type="error">{error}</StatusIndicator>
            ) : (
              'No base maps found'
            )}
          </Box>
          <Button onClick={() => navigate('/admin/base-maps/create')}>
            Create Base Map
          </Button>
        </Box>
      }
    />
  );
};

export default BaseMapsList;
